const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// Default metallic gradients — the SAME surface used by the Home tier card, so
// the Profile button and the Points card can mirror it exactly (Point 40).
export const TIER_GRADIENT = {
  Silver: "from-[#B6C2D0] via-[#8C99A8] to-[#3A4555]",
  Gold: "from-[#FBE38B] via-[#D4A93A] to-[#8C6618]",
  Platinum: "from-[#2B50B0] via-[#173075] to-[#0A1736]",
};

const DEFAULT_ORDER = ["Silver", "Gold", "Platinum"];
const DEFAULT_THRESHOLDS = { Silver: 0, Gold: 5000, Platinum: 15000 };

function isDarkHex(hex) {
  if (!hex || typeof hex !== "string") return false;
  const h = hex.replace("#", "");
  if (h.length < 6) return false;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  // relative luminance
  return (0.299 * r + 0.587 * g + 0.114 * b) < 140;
}

// Compute a consistent tier surface + progress from the admin app-config so that
// the Home card, top-right Profile button and Points card all match (Point 40),
// and every tier NAME comes from config (Point 38).
export function computeTierStyle(userTier, totalEarned, cfg) {
  const tiers = (cfg && cfg.tiers) || [];
  const sorted = [...tiers].sort((a, b) => (a.min_points || 0) - (b.min_points || 0));
  let curCfg = null, nextCfg = null;
  if (sorted.length) {
    let idx = sorted.findIndex(t => (t.name || "").toLowerCase() === (userTier || "").toLowerCase());
    if (idx < 0) { idx = 0; for (let i = 0; i < sorted.length; i++) { if ((totalEarned || 0) >= (sorted[i].min_points || 0)) idx = i; } }
    curCfg = sorted[idx];
    nextCfg = sorted[idx + 1] || null;
  }

  const tierName = (curCfg && curCfg.name) || userTier || "Silver";
  const design = curCfg
    ? { card_image_url: curCfg.card_image_url, color_text: curCfg.color_text, color_accent: curCfg.color_accent, color_primary: curCfg.color_primary }
    : (((cfg && cfg.tier_design) || {})[userTier] || {});

  const customImg = design.card_image_url
    ? (design.card_image_url.startsWith("http") ? design.card_image_url : `${BACKEND}${design.card_image_url}`)
    : "";
  const colorPrimary = design.color_primary || "";
  const colorText = design.color_text || "";
  const colorAccent = design.color_accent || "";
  const gradientClass = TIER_GRADIENT[tierName] || TIER_GRADIENT[userTier] || TIER_GRADIENT.Silver;
  const isDark = tierName === "Platinum" || !!customImg || isDarkHex(colorPrimary);

  // thresholds & progress
  const orderedNames = sorted.length ? sorted.map(t => t.name) : DEFAULT_ORDER;
  const steps = sorted.length
    ? sorted.map(t => ({ name: t.name, min: t.min_points || 0 }))
    : DEFAULT_ORDER.map(n => ({ name: n, min: DEFAULT_THRESHOLDS[n] || 0 }));
  const curIndex = orderedNames.indexOf(tierName);
  const curMin = curCfg ? (curCfg.min_points || 0) : (DEFAULT_THRESHOLDS[tierName] || 0);
  const nextTierName = curCfg
    ? (nextCfg ? nextCfg.name : null)
    : (curIndex >= 0 && curIndex < DEFAULT_ORDER.length - 1 ? DEFAULT_ORDER[curIndex + 1] : null);
  const nextMin = nextCfg
    ? (nextCfg.min_points || 0)
    : (nextTierName ? (DEFAULT_THRESHOLDS[nextTierName] || curMin) : curMin);
  const te = totalEarned || 0;
  const pointsToNext = nextMin > curMin ? Math.max(0, nextMin - te) : 0;
  const progressPercent = nextMin > curMin ? Math.min(100, ((te - curMin) / (nextMin - curMin)) * 100) : 100;

  return {
    tierName, customImg, colorPrimary, colorText, colorAccent, gradientClass, isDark,
    orderedNames, steps, curIndex, curMin, nextTierName, nextMin, pointsToNext, progressPercent,
  };
}

// Inline style object that reproduces the Home card surface for smaller elements
// (e.g. the top-right profile button). Uses the admin card image if set, else the
// caller falls back to the tier gradient classes — exactly like the Home card.
export function tierSurfaceStyle(ts) {
  if (ts.customImg) {
    return { backgroundImage: `url(${ts.customImg})`, backgroundSize: "cover", backgroundPosition: "center" };
  }
  return null; // caller uses the gradientClass tailwind classes instead
}
